import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { getApiDevosProgress, getGetApiDevosProgressQueryOptions } from "twi-client";

import { queryClient } from "~/entry.client";
import type { ProgressByEpisodeItem } from "~/lib/series.utils";
import { normalizeProgressResponse } from "~/services/progress.client";

export const PROGRESS_QUERY_KEY = {
  all: ["episodes-progress"],
};

const progressApiQueryKey = getGetApiDevosProgressQueryOptions(undefined, {
  query: {
    queryKey: PROGRESS_QUERY_KEY.all,
  },
}).queryKey;

export const useProgressQueryOptions = {
  queryKey: progressApiQueryKey,
  queryFn: async ({ signal }) => {
    try {
      const data = await getApiDevosProgress(undefined, signal);
      return normalizeProgressResponse(data);
    } catch (error) {
      console.error("Error fetching devo progress:", error);
      return [];
    }
  },
} satisfies UseQueryOptions<Array<ProgressByEpisodeItem>, Error>;
export function useProgressQuery(options: UseQueryOptions<Array<ProgressByEpisodeItem>, Error>) {
  return useQuery({ ...useProgressQueryOptions, ...options });
}
export function ensureProgressData() {
  return queryClient.ensureQueryData(useProgressQueryOptions);
}

export function invalidateProgressQuery() {
  return queryClient.invalidateQueries(useProgressQueryOptions);
}

/**
 * Optimistically updates the general progress query with current progress
 * @param cmsId - The CMS ID of the episode
 * @param progressPercentage - The current progress percentage
 * @param completedAtUtc - The UTC timestamp when the episode was completed (only when 100%)
 * @param lastListenedUtc - The UTC timestamp when the episode was last listened to
 */
export function optimisticallyUpdateProgressQuery(
  cmsId: string,
  progressPercentage: number,
  completedAtUtc?: string,
  lastListenedUtc?: string,
): void {
  queryClient.setQueryData<Array<ProgressByEpisodeItem>>(useProgressQueryOptions.queryKey, (oldData = []) => {
    if (!Array.isArray(oldData)) return [];

    const exists = oldData.some((item) => item.cmsId === cmsId);

    if (exists) {
      return oldData.map((item) =>
        item.cmsId === cmsId ? { ...item, progressPercentage, completedAtUtc, lastListenedUtc } : item,
      );
    }

    return [
      ...oldData,
      {
        cmsId,
        progressPercentage,
        completedAtUtc,
        lastListenedUtc,
      } as ProgressByEpisodeItem,
    ];
  });
}
